import { useState, useCallback } from "react";
import { deleteProvider } from "services/paymentApi";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../../utils/errorHandler";
import type { ProviderAdditionalStep } from "services/paymentApi.types";
import { ExistingProvider } from "../types";

interface UseProviderModalsProps {
  dealerId: string;
  existingProvider: ExistingProvider | null;
  onProviderDeleted?: (providerId: string) => void;
  onSuccess?: () => void;
}

export function useProviderModals({
  dealerId,
  existingProvider,
  onProviderDeleted,
  onSuccess,
}: UseProviderModalsProps) {
  const [showCancelProvider, setShowCancelProvider] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [showDeleteProvider, setShowDeleteProvider] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const [additionalStep, setAdditionalStep] =
    useState<ProviderAdditionalStep | null>(null);

  const handleCancelProvider = useCallback(() => {
    setShowCancelProvider(!showCancelProvider);
  }, [showCancelProvider]);

  const handleCloseValidationError = useCallback(() => {
    setShowValidationError(false);
  }, []);

  const handleDeleteProvider = useCallback(() => {
    setShowDeleteProvider(!showDeleteProvider);
  }, [showDeleteProvider]);

  const handleCloseDeleteModal = useCallback(() => {
    if (!isDeleting) {
      setShowDeleteProvider(false);
    }
  }, [isDeleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!existingProvider?.id || !dealerId) return;

    setIsDeleting(true);
    try {
      await deleteProvider({
        merchantId: dealerId,
        providerId: existingProvider.id,
      });

      toast("Provedor excluído com sucesso!", { type: "success" });
      setShowDeleteProvider(false);
      onProviderDeleted?.(existingProvider.id);
      onSuccess?.();
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível excluir o provedor. Tente novamente!",
      );
      toast(errorMessage, { type: "error" });
    } finally {
      setIsDeleting(false);
    }
  }, [dealerId, existingProvider, onProviderDeleted, onSuccess]);

  const handleCloseConfirmSave = useCallback((isSaving: boolean) => {
    if (!isSaving) {
      setShowConfirmSave(false);
    }
  }, []);

  const handleCloseSuccessMessage = useCallback(() => {
    setShowSuccessMessage(false);
  }, []);

  const handleCloseAdditionalStepModal = useCallback(() => {
    setAdditionalStep(null);
  }, []);

  return {
    showCancelProvider,
    showValidationError,
    showDeleteProvider,
    showSuccessMessage,
    isDeleting,
    showConfirmSave,
    additionalStep,
    setShowValidationError,
    setShowConfirmSave,
    setShowSuccessMessage,
    setAdditionalStep,
    handleCancelProvider,
    handleCloseValidationError,
    handleDeleteProvider,
    handleCloseDeleteModal,
    handleConfirmDelete,
    handleCloseConfirmSave,
    handleCloseSuccessMessage,
    handleCloseAdditionalStepModal,
  };
}
